import { transparentize, rem } from 'polished';
import styled, { css, keyframes } from 'styled-components';

export const SROnly = styled.span`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  top: 0;
  left: 0;
  white-space: nowrap;
  width: 1px;
`;

export function sharedTransition(properties) {
  return css`
    transition: 0.3s ease;
    transition-property: ${properties};
  `;
}

export function focusVisible(rules) {
  return css`
    .js-focus-visible &:focus {
      outline: none;
    }

    &:focus-visible {
      ${rules}
    }

    &:focus.focus-visible {
      ${rules}
    }
  `;
}

const tones = (colors) => ({
  primary: {
    main: colors.irisBlue,
    pair: colors.blank,
  },
  inverted: {
    main: colors.blank,
    pair: colors.irisBlue,
  },
  secondary: {
    main: colors.darkTurquoise,
    pair: colors.blank,
  },
  destructive: {
    main: colors.redPink,
    pair: colors.blank,
  },
  subtle: {
    main: colors.lynch,
    pair: colors.blank,
  },
});

const focusRingBorderCSS = (colorMain) => css`
  border-color: ${colorMain};
  box-shadow: 0 0 0 3px ${transparentize(0.7, colorMain)};
`;

const ButtonBase = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  color: inherit;
  background-color: transparent;
  border: none;
  padding: 0;
  text-decoration: none;
  font-family: inherit;
  font-weight: 500;
  justify-content: center;
`;

// ============= Button (Main) ============== //

const btnMainSizes = {
  lg: css`
    padding: 0 32px;
    min-height: 48px;
    font-size: ${rem('16px')};
    min-width: 200px;
  `,
  md: css`
    padding: 0 24px;
    min-height: 44px;
    font-size: ${rem('16px')};
    min-width: 140px;
  `,
  sm: css`
    padding: 0 22px;
    min-height: 40px;
    font-size: ${rem('14px')};
    min-width: 120px;
  `,
  xs: css`
    padding: 0 18px;
    min-height: 34px;
    font-size: ${rem('14px')};
    min-width: 100px;
  `,
};

export const ButtonMain = styled(ButtonBase)`
  /* Due to ButtonBase styles sometimes take precendence over ButtonMain styles, we're forced
   * for now to increase the specifity of ButtonMain to make it work without any issues.
   * This is a known issue with SSR and styled-components. Check https://www.notion.so/remotecom/Dragon-Troubleshooting-5d26da7c00514dc4b5238e7e63942fc9
   * for more details.
   */
  && {
    line-height: 1; /* prevent breaking the layout if it has 2 lines */

    border-radius: 50px;
    /* Set an border invisible so that all variants look similar
   in width, regardless if they have or not an actual border. */
    border: 2px solid transparent;
    ${sharedTransition('color, background-color, border-color, opacity')};

    color: var(--button-colorText);

    ${({ $size }) => btnMainSizes[$size]};

    ${({ theme, $variant, $tone, $shadow }) => {
      const colors = tones(theme.colors)[$tone];

      switch ($variant) {
        case 'solid':
          return css`
            --button-colorText: ${colors.pair};
            background-color: ${colors.main};

            &:hover {
              ${$tone === 'inverted'
                ? css`
                    opacity: 0.7;
                  `
                : css`
                    filter: saturate(1.4);
                  `}
            }

            ${focusVisible(css`
              box-shadow: 0 0 0 3px ${colors.pair}, 0 0 0 5px ${colors.main};
            `)};

            ${$shadow &&
            css`
              box-shadow: 0px 6px 12px ${transparentize(0.7, colors.main)};
            `}
          `;
        case 'outline':
          return css`
            --button-colorText: ${colors.main};

            background-color: ${colors.pair};
            border-color: ${transparentize(0.55, colors.main)};

            &:hover {
              border-color: ${colors.main};
              background-color: ${transparentize(0.85, colors.main)};
            }

            ${focusVisible(css`
              background-color: ${colors.pair};
              ${focusRingBorderCSS(colors.main)};
            `)};
          `;
        case 'ghost':
        case 'ghost-caps':
          return css`
            --button-colorText: ${colors.main};

            &:hover {
              filter: brightness(0.75);
              background-color: ${transparentize(0.85, colors.main)};
            }

            ${focusVisible(css`
              ${focusRingBorderCSS(colors.main)};
            `)};

            ${$variant === 'ghost-caps' &&
            css`
              font-weight: 600;
              font-size: ${rem('12px')};
              text-transform: uppercase;
            `}
          `;
        default:
          return null;
      }
    }}

    ${({ $isLoading }) =>
      $isLoading &&
      css`
        pointer-events: none;
        color: transparent;
      `}
  }
`;

export const IconAddon = styled.svg`
  width: 20px;
  flex-shrink: 0;

  ${({ $place }) => {
    switch ($place) {
      case 'before':
        return css`
          margin-right: 8px;
        `;
      case 'after':
      default:
        return css`
          margin-left: 8px;
        `;
    }
  }}
`;

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

export const LoadingAddon = styled.span`
  position: absolute;
  display: block;
  width: 1em;
  height: 1em;
  top: calc(50% - 0.5em);
  left: calc(50% - 0.5em);
  border-width: 2px;
  border-color: var(--button-colorText);
  border-bottom-color: transparent;
  border-left-color: transparent;
  border-style: solid;
  border-radius: 50%;
  animation: ${spin} 0.45s linear infinite;
`;

// ============= ButtonInline ============== //

export const ButtonInline = styled(ButtonBase)`
  /* Due to ButtonBase styles sometimes take precendence over ButtonInline styles, we're forced
   * for now to increase the specifity of ButtonInline to make it work without any issues.
   * This is a known issue with SSR and styled-components. Check https://www.notion.so/remotecom/Dragon-Troubleshooting-5d26da7c00514dc4b5238e7e63942fc9
   * for more details.
   */
  && {
    position: relative;
    display: inline;

    color: var(--button-colorText);

    &:hover {
      filter: saturate(1.4);
    }

    &:hover,
    &:focus {
      text-decoration: underline;
      text-decoration-thickness: 2px;
    }

    ${({ theme }) => css`
      ${focusVisible(css`
        background-color: ${theme.colors.irisBlue_15};
      `)};
    `}

    ${({ theme, $tone }) => {
      switch ($tone) {
        case 'primary':
          return css`
            --button-colorText: var(--colors-irisBlue);
            --button-lineColor: ${theme.colors.irisBlue_75};
          `;
        case 'inverted':
          return css`
            --button-colorText: var(--colors-blank);
            --button-lineColor: ${theme.colors.blank};
          `;
        default:
          return null;
      }
    }}

  ${({ $variant }) => {
      switch ($variant) {
        case 'underline':
          return css`
            text-decoration: underline;
            text-decoration-color: var(--button-lineColor);
          `;
        case 'inline':
        default:
          return null;
      }
    }}
  }
`;

// ============= ButtonIcon ============== //

const btnIconSizes = {
  md: '44px',
  sm: '32px',
};

const ButtonIconCircleEl = styled.span``;
const ButtonLabelEl = styled.span``;

export const ButtonIconArea = styled(ButtonBase)`
  color: var(--button-colorText);

  &:hover ${ButtonLabelEl} {
    filter: brightness(0.75);
  }

  ${({ $size }) => css`
    --buttonIcon-size: ${btnIconSizes[$size]};
  `};

  /* used to remove default outline */
  ${focusVisible()}

  ${({ theme, $variant, $tone }) => {
    const colors = tones(theme.colors)[$tone];

    switch ($variant) {
      case 'outline':
        return css`
          ${ButtonIconCircleEl} {
            border-color: ${transparentize(0.55, colors.main)};
          }

          &:hover ${ButtonIconCircleEl} {
            border-color: ${colors.main};
          }

          ${focusVisible(css`
            ${ButtonIconCircleEl} {
              background-color: ${colors.pair};
            }
          `)};
        `;
      case 'ghost':
        return css`
          &:hover ${ButtonIconCircleEl} {
            background-color: ${transparentize(0.85, colors.main)};
          }
        `;
      default:
        return null;
    }
  }};

  ${({ theme, $tone }) => {
    const colors = tones(theme.colors)[$tone];

    return css`
      --button-colorText: ${colors.main};

      &:hover ${ButtonIconCircleEl} {
        background-color: ${transparentize(0.85, colors.main)};
      }

      ${focusVisible(css`
        ${ButtonIconCircleEl} {
          ${focusRingBorderCSS(colors.main)};
        }
      `)};
    `;
  }};
`;

export const ButtonIconCircle = styled(ButtonIconCircleEl)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0; /* in case it's used as a flex child */
  fill: currentColor;
  border: 2px solid transparent;
  border-radius: 50%;
  ${sharedTransition('color, background-color, border-color')};

  width: var(--buttonIcon-size);
  height: var(--buttonIcon-size);

  &:first-child:not(:last-child) {
    margin-right: 8px;
  }

  &:last-child:not(:first-child) {
    margin-left: 8px;
  }
`;

export const ButtonLabel = styled(ButtonLabelEl)``;
